// Beneficiaries API (spec §2, §4–6). Add and edit are each a single atomic
// call (§5.4, §17.9). OTP for the save/delete approval lives here too (§9.3).
import type {
  Beneficiary,
  BeneficiaryAccount,
  BeneficiaryAccountStatus,
  CreateBeneficiaryInput,
  MobileSubType,
  NewAccountInput,
  UpdateBeneficiaryInput,
  VerifyResult,
} from '@/types/phase2.types';
import { ApiError, del, get, patch, post } from './client';
import { MOCK_BENEFICIARIES, REAL_API_ENABLED } from './flags';
import { generateStepUpOtp, toFriendlyOtpError, verifyStepUpOtp, type StepUpOtpSession } from './auth';
import * as mock from '@/mock/handlers/beneficiaries';
import { resendOtpSession, startOtpSession, verifyOtpSession } from '@/mock/handlers/otp';

export interface BeneficiaryApiAccount {
  id?: string;
  addedAt?: string;
  status?: string;
  activatingUntil?: string;
  limitedUntil?: string;
  paymentType: string;
  upiKind?: string;
  holderName?: string;
  vpa?: string;
  mobileNumber?: string;
  merchantName?: string;
  operatorName?: string;
  circle?: string;
  bbpsCategory?: string;
  biller?: string;
  consumerNumber?: string;
  bankName?: string;
  ifsc?: string;
  branch?: string;
  accountNumber?: string;
  accountName?: string;
  countryIso?: string;
  iban?: string;
  routingCode?: string;
  swiftBic?: string;
  currency?: string;
  bankLine1?: string;
  bankLine2?: string;
  bankPostCode?: string;
  bankTown?: string;
  beneficiaryLine1?: string;
  beneficiaryLine2?: string;
  beneficiaryPostCode?: string;
  beneficiaryTown?: string;
}

export interface BeneficiaryApi {
  id: string;
  alias: string;
  accounts: BeneficiaryApiAccount[];
  [key: string]: unknown;
}

export interface CreateBeneficiaryApiInput {
  alias: string;
  notifyEmail?: string;
  notifyMobile?: string;
  initialAccount?: BeneficiaryApiAccount;
}

export interface UpdateBeneficiaryApiInput {
  alias?: string;
  notifyEmail?: string;
  notifyMobile?: string;
}

export interface BeneficiaryAuditEntry {
  id: string;
  beneficiaryId?: string;
  action?: string;
  [key: string]: unknown;
}

const beneficiaryPath = (id: string): string =>
  `/beneficiaries/${encodeURIComponent(id)}`;

const beneficiariesUnavailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The beneficiaries API is not available yet.');
};

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The beneficiaries API is not available yet.');
};

// ---------- server rows <-> UI model ----------

/** Server timestamp (ISO string) to epoch milliseconds. */
const ms = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const time = Date.parse(value);
  return Number.isNaN(time) ? undefined : time;
};

const ACCOUNT_STATUSES: BeneficiaryAccountStatus[] = ['ACTIVATING', 'LIMITED', 'ACTIVE'];

const fromApiAccount = (api: BeneficiaryApiAccount): BeneficiaryAccount => {
  const id = String(api.id ?? '');
  const rawStatus = String(api.status ?? '').toUpperCase() as BeneficiaryAccountStatus;
  const status = {
    status: ACCOUNT_STATUSES.includes(rawStatus) ? rawStatus : 'ACTIVE',
    activeAt: ms(api.activatingUntil),
    limitedUntil: ms(api.limitedUntil),
  };
  switch (api.paymentType) {
    case 'UPI_MOBILE':
      return {
        kind: 'MOBILE_TRANSFER',
        id,
        subType: (api.upiKind ?? 'UPI_ID') as MobileSubType,
        holderName: api.holderName ?? '',
        upiId: api.vpa ?? undefined,
        mobile: api.mobileNumber ?? undefined,
        merchantName: api.merchantName ?? undefined,
        operator: api.operatorName ?? undefined,
        circle: api.circle ?? undefined,
        billCategory: api.bbpsCategory ?? undefined,
        biller: api.biller ?? undefined,
        consumerNumber: api.consumerNumber ?? undefined,
        status,
      };
    case 'INTERNATIONAL':
      return {
        kind: 'INTERNATIONAL',
        id,
        countryCode: api.countryIso ?? '',
        bankName: api.bankName ?? '',
        accountName: api.accountName ?? '',
        iban: api.iban ?? undefined,
        accountNumber: api.accountNumber ?? undefined,
        routingCode: api.routingCode ?? undefined,
        swift: api.swiftBic ?? undefined,
        currency: api.currency ?? '',
        accountAddress: { line1: '' },
        status,
      };
    case 'WITHIN_INDIA':
    default: {
      const ifsc = api.ifsc ?? '';
      return {
        kind: 'LOCAL_TRANSFER',
        id,
        bankCode: ifsc.slice(0, 4),
        bankName: api.bankName ?? '',
        branchName: api.branch ?? undefined,
        ifsc,
        accountNumber: api.accountNumber ?? '',
        accountName: api.accountName ?? '',
        status,
      };
    }
  }
};

/** Maps a GET/POST /beneficiaries row to the UI model the screens read. */
export const fromApiBeneficiary = (api: BeneficiaryApi): Beneficiary => {
  const accounts = Array.isArray(api.accounts) ? api.accounts : [];
  const added = accounts.map((a) => ms(a.addedAt)).filter((t): t is number => t !== undefined);
  const createdAt = added.length > 0 ? Math.min(...added) : 0;
  return {
    id: String(api.id ?? ''),
    alias: String(api.alias ?? ''),
    accounts: accounts.map(fromApiAccount),
    createdAt,
    updatedAt: createdAt,
  };
};

/** The reverse of fromApiAccount: a form account to the server request. */
const toApiAccount = (account: NewAccountInput): BeneficiaryApiAccount => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER':
      return {
        paymentType: 'UPI_MOBILE',
        upiKind: account.subType,
        holderName: account.holderName,
        vpa: account.upiId,
        mobileNumber: account.mobile,
        merchantName: account.merchantName,
        operatorName: account.operator,
        circle: account.circle,
        bbpsCategory: account.billCategory,
        biller: account.biller,
        consumerNumber: account.consumerNumber,
      };
    case 'LOCAL_TRANSFER':
      return {
        paymentType: 'WITHIN_INDIA',
        bankName: account.bankName,
        ifsc: account.ifsc,
        branch: account.branchName,
        accountNumber: account.accountNumber,
        accountName: account.accountName,
      };
    case 'INTERNATIONAL':
      return {
        paymentType: 'INTERNATIONAL',
        countryIso: account.countryCode,
        bankName: account.bankName,
        accountName: account.accountName,
        iban: account.iban,
        accountNumber: account.accountNumber,
        routingCode: account.routingCode,
        swiftBic: account.swift,
        currency: account.currency,
        bankLine1: account.bankAddress?.line1,
        bankLine2: account.bankAddress?.line2,
        bankPostCode: account.bankAddress?.postCode,
        bankTown: account.bankAddress?.town,
        beneficiaryLine1: account.accountAddress.line1,
        beneficiaryLine2: account.accountAddress.line2,
        beneficiaryPostCode: account.accountAddress.postCode,
        beneficiaryTown: account.accountAddress.town,
      };
  }
};

const realCreateBeneficiary = async (input: CreateBeneficiaryInput): Promise<Beneficiary> => {
  const [first, ...rest] = input.accounts;
  let saved = await addBeneficiary({
    alias: input.alias,
    initialAccount: first ? toApiAccount(first) : undefined,
  });
  for (const account of rest) {
    saved = await addBeneficiaryAccount(saved.id, toApiAccount(account));
  }
  return fromApiBeneficiary(saved);
};

const realLookupAccountName = async (ifsc: string, accountNumber: string): Promise<VerifyResult> => {
  const data = await get<{ resolved?: boolean; holderName?: string | null }>('/payees/name-enquiry/account', {
    params: { accountNumber, ifsc },
  });
  return { outcome: data.resolved ? 'FOUND' : 'NOT_FOUND', holderName: data.holderName ?? undefined };
};

export const listBeneficiaries = (): Promise<Beneficiary[]> =>
  MOCK_BENEFICIARIES
    ? mock.listBeneficiaries()
    : REAL_API_ENABLED
      ? get<BeneficiaryApi[]>('/beneficiaries').then((rows) => (Array.isArray(rows) ? rows : []).map(fromApiBeneficiary))
      : notAvailable();

export const createBeneficiary = (input: CreateBeneficiaryInput): Promise<Beneficiary> =>
  MOCK_BENEFICIARIES
    ? mock.createBeneficiary(input)
    : REAL_API_ENABLED
      ? realCreateBeneficiary(input)
      : notAvailable();

export const getBeneficiaries = (): Promise<BeneficiaryApi[]> =>
  REAL_API_ENABLED ? get<BeneficiaryApi[]>('/beneficiaries') : beneficiariesUnavailable();

export const addBeneficiary = (input: CreateBeneficiaryApiInput): Promise<BeneficiaryApi> =>
  REAL_API_ENABLED ? post<BeneficiaryApi>('/beneficiaries', input) : beneficiariesUnavailable();

export const addBeneficiaryAccount = (
  beneficiaryId: string,
  input: BeneficiaryApiAccount,
): Promise<BeneficiaryApi> =>
  REAL_API_ENABLED
    ? post<BeneficiaryApi>(`${beneficiaryPath(beneficiaryId)}/accounts`, input)
    : beneficiariesUnavailable();

export const getBeneficiary = (beneficiaryId: string): Promise<BeneficiaryApi> =>
  REAL_API_ENABLED
    ? get<BeneficiaryApi>(beneficiaryPath(beneficiaryId))
    : beneficiariesUnavailable();

export const updateBeneficiaryApi = (
  beneficiaryId: string,
  input: UpdateBeneficiaryApiInput,
): Promise<BeneficiaryApi> =>
  REAL_API_ENABLED
    ? patch<BeneficiaryApi>(beneficiaryPath(beneficiaryId), input)
    : beneficiariesUnavailable();

export const deleteBeneficiaryApi = (beneficiaryId: string): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? del<Record<string, unknown>>(beneficiaryPath(beneficiaryId))
    : beneficiariesUnavailable();

export const listBeneficiaryAudit = (): Promise<BeneficiaryAuditEntry[]> =>
  REAL_API_ENABLED
    ? get<BeneficiaryAuditEntry[]>('/beneficiaries/audit')
    : beneficiariesUnavailable();

export const removeBeneficiaryAccount = (
  beneficiaryId: string,
  accountId: string,
): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? del<Record<string, unknown>>(
        `${beneficiaryPath(beneficiaryId)}/accounts/${encodeURIComponent(accountId)}`,
      )
    : beneficiariesUnavailable();

export const updateBeneficiary = (id: string, input: UpdateBeneficiaryInput): Promise<Beneficiary> =>
  MOCK_BENEFICIARIES ? mock.updateBeneficiary(id, input) : notAvailable();

export const deleteBeneficiary = (id: string): Promise<{ alias: string }> =>
  MOCK_BENEFICIARIES ? mock.deleteBeneficiary(id) : notAvailable();

export const verifyUpiId = (upiId: string): Promise<VerifyResult> =>
  MOCK_BENEFICIARIES
    ? mock.verifyUpiId(upiId)
    : REAL_API_ENABLED
      ? get<VerifyResult>(`/payees/name-enquiry/upi?upiId=${encodeURIComponent(upiId.trim())}`)
      : notAvailable();

export const verifyMobile = (mobile: string): Promise<VerifyResult> =>
  MOCK_BENEFICIARIES
    ? mock.verifyMobile(mobile)
    : REAL_API_ENABLED
      ? get<VerifyResult>(`/payees/name-enquiry/mobile?mobileNumber=${encodeURIComponent(mobile.trim())}`)
      : notAvailable();

export const lookupAccountName = (ifsc: string, accountNumber: string): Promise<VerifyResult> =>
  MOCK_BENEFICIARIES
    ? mock.lookupAccountName(ifsc, accountNumber)
    : REAL_API_ENABLED
      ? realLookupAccountName(ifsc, accountNumber)
      : notAvailable();

// Beneficiary add/edit/delete approve with OTP only (§9). On the real API the
// OTP is the server's step-up pair (PAYEE_MANAGE): /beneficiaries does not
// check a code itself, so a verified OTP is the gate before the save call.
export interface BeneficiaryOtpSession {
  authId: string;
  expiresInSeconds: number;
  maskedDestination?: string;
}

const PAYEE_OTP_TYPE = 'PAYEE_MANAGE';
const DEFAULT_OTP_SECONDS = 180;

// authId (the first transactionId) -> the latest server session. A resend
// gets a new transactionId while the screens keep the first authId.
const payeeOtpSessions = new Map<string, StepUpOtpSession>();

const realRequestBeneficiaryOtp = async (): Promise<BeneficiaryOtpSession> => {
  try {
    const session = await generateStepUpOtp({ transactionType: PAYEE_OTP_TYPE });
    payeeOtpSessions.set(session.transactionId, session);
    return {
      authId: session.transactionId,
      expiresInSeconds: session.expiresInSeconds ?? DEFAULT_OTP_SECONDS,
      maskedDestination: session.maskedDestination,
    };
  } catch (err) {
    throw toFriendlyOtpError(err);
  }
};

const realResendBeneficiaryOtp = async (authId: string): Promise<{ expiresInSeconds: number }> => {
  try {
    const session = await generateStepUpOtp({ transactionType: PAYEE_OTP_TYPE });
    payeeOtpSessions.set(authId, session);
    return { expiresInSeconds: session.expiresInSeconds ?? DEFAULT_OTP_SECONDS };
  } catch (err) {
    throw toFriendlyOtpError(err);
  }
};

const realVerifyBeneficiaryOtp = async (authId: string, code: string): Promise<void> => {
  const session = payeeOtpSessions.get(authId);
  try {
    // The response is a step-up token pair; it is deliberately not kept.
    await verifyStepUpOtp({
      transactionId: session?.transactionId ?? authId,
      code,
      transactionType: PAYEE_OTP_TYPE,
      deliveryMethod: session?.deliveryMethod ?? 'SMS',
    });
  } catch (err) {
    throw toFriendlyOtpError(err);
  }
  payeeOtpSessions.delete(authId);
};

export const requestBeneficiaryOtp = (): Promise<BeneficiaryOtpSession> =>
  REAL_API_ENABLED ? realRequestBeneficiaryOtp() : startOtpSession('BENEFICIARY');

export const resendBeneficiaryOtp = (authId: string): Promise<{ expiresInSeconds: number }> =>
  REAL_API_ENABLED ? realResendBeneficiaryOtp(authId) : resendOtpSession(authId);

export const verifyBeneficiaryOtp = (authId: string, code: string): Promise<void> =>
  REAL_API_ENABLED ? realVerifyBeneficiaryOtp(authId, code) : verifyOtpSession(authId, code);
